import React, { Fragment, useEffect, useRef, useState } from "react";
import { Button, FormGroup, Label, Row, Col } from 'reactstrap';
import Select, { components } from "react-select";

const FabricTable = (props) => {
    const { 
        getValues, setValue, trigger, errors, settingFabricTypes, settingMainFabrics, settingSubFabrics, settingSustainabilityInfo, settingCommodities,
        fabricRows, setFabricRows 
    } = props;

    // New: duplicate tracking state
    const [duplicateIndices, setDuplicateIndices] = useState([]);

    const handleFabricRowChange = (idx, field, value) => {
        // Reset dependent fields when parent changes
        if (field === 'fabric_type') {
            setFabricRows(prev => prev.map((row, i) => i === idx ? { ...row, fabric_type: value, main_fabric: '', sub_fabric: [] } : row));
        } else if (field === 'main_fabric') {
            setFabricRows(prev => prev.map((row, i) => i === idx ? { ...row, main_fabric: value, sub_fabric: [] } : row));
        } else {
            setFabricRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
        }
    };

    const handleAddFabricRow = () => {
        setFabricRows(prev => ([...prev, { fabric_type: '', main_fabric: '', sub_fabric: [] }]));
    };

    const handleRemoveFabricRow = (idx) => {
        setFabricRows(prev => prev.length > 0 ? prev.filter((_, i) => i !== idx) : prev);
    };

    useEffect(() => {
        setValue("fabrics", [...fabricRows]);
        trigger("fabrics");
    }, [fabricRows]);

    // New: detect duplicate rows & expose a form-level flag
    useEffect(() => {
        const keyMap = {};
        (fabricRows || []).forEach((row, idx) => {
            // const sub = (row.sub_fabric || []).slice().sort().join(',');
            // const key = `${row.fabric_type || ''}|${row.main_fabric || ''}|${sub}`;
            const key = `${row.fabric_type || ''}|${row.main_fabric || ''}`;
            const isEmpty = !row.fabric_type && !row.main_fabric && (!row.sub_fabric || row.sub_fabric.length === 0);
            if (isEmpty) return; // ignore totally empty rows
            if (!keyMap[key]) keyMap[key] = [];
            keyMap[key].push(idx);
        });
        const duplicates = new Set();
        Object.values(keyMap).forEach(arr => { if (arr.length > 1) arr.forEach(i => duplicates.add(i)); });
        const dupArray = Array.from(duplicates);
        setDuplicateIndices(dupArray);
        const hasDuplicates = dupArray.length > 0;
        if (setValue) setValue("fabrics_has_duplicates", hasDuplicates);
        if (trigger) trigger("fabrics_has_duplicates");
    }, [fabricRows, setValue, trigger]);

    return (
        <FormGroup>
            <Label className="form-label ai-form-heading">Fabric Details</Label>
            <Row>
                {/* Sustainability Info (Multi Select) */}
                <Col md="">
                    <FormGroup>
                        <Label className="form-label">Sustainability Info</Label>
                        <Select
                            placeholder="Select Sustainability Info"
                            isMulti
                            options={settingSustainabilityInfo || []}
                            getOptionValue={option => option.id}
                            getOptionLabel={option => option.name}
                            value={settingSustainabilityInfo?.filter(opt => (getValues("sustainability_records") || []).includes(opt.id))}
                            onChange={selected => { setValue("sustainability_records", selected ? selected.map(s => s.id) : []); trigger("sustainability_records"); }}
                            className={errors.sustainability_records ? "is-invalid" : ""}
                            isClearable={true}
                            components={{
                                IndicatorSeparator: () => null,
                                DropdownIndicator: (props) => {
                                    return getValues("sustainability_records") ? null : <components.DropdownIndicator {...props} />;
                                }
                            }}
                        />
                        <div className="invalid-feedback">{errors.sustainability_records?.message}</div>
                    </FormGroup>
                </Col>
                {/* Commodity (Multi Select) */}
                <Col md="">
                    <FormGroup>
                        <Label className="form-label">Commodity</Label>
                        <Select
                            placeholder="Select Commodity"
                            isMulti
                            options={settingCommodities || []}
                            getOptionValue={option => option.id}
                            getOptionLabel={option => option.name}
                            value={settingCommodities?.filter(opt => (getValues("commodities") || []).includes(opt.id))}
                            onChange={selected => { setValue("commodities", selected ? selected.map(s => s.id) : []); trigger("commodities"); }}
                            className={errors.commodities ? "is-invalid" : ""}
                            isClearable={true}
                            components={{
                                IndicatorSeparator: () => null,
                                DropdownIndicator: (props) => {
                                    return getValues("commodities") ? null : <components.DropdownIndicator {...props} />;
                                }
                            }}
                        />
                        <div className="invalid-feedback">{errors.commodities?.message}</div>
                    </FormGroup>
                </Col>
            </Row>
            <table className="table table-bordered table-sm align-middle">
                <thead>
                    <tr>
                        <th style={{ width: '25%' }}>Fabric Type</th>
                        <th style={{ width: '25%' }}>Main Fabric</th>
                        <th style={{ width: '40%' }}>Sub Fabric</th>
                        <th style={{ width: '40%' }}>Compositions</th>
                        <th style={{ width: '10%' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {(fabricRows || []).map((row, idx) => {
                        const isDuplicate = duplicateIndices.includes(idx);

                        // helper to pass styles to react-select so only the select control shows error state
                        const selectErrorStyles = {
                            control: (base, state) => ({
                                ...base,
                                borderColor: isDuplicate ? '#dc3545' : base.borderColor,
                                boxShadow: isDuplicate ? '0 0 0 1px rgba(220,53,69,0.12)' : base.boxShadow,
                                '&:hover': { borderColor: isDuplicate ? '#dc3545' : base.borderColor },
                            })
                        };

                        return (
                            <tr key={idx}>
                                <td>
                                    <Select
                                        placeholder="Select Fabric Type"
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        options={settingFabricTypes}
                                        value={settingFabricTypes.find(opt => opt.id === row.fabric_type) || null}
                                        onChange={selected => handleFabricRowChange(idx, 'fabric_type', selected ? selected.id : '')}
                                        styles={selectErrorStyles}
                                    />
                                </td>
                                <td>
                                    <Select
                                        placeholder="Select Main Fabric"
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        options={settingMainFabrics.filter(opt => opt.fabric_type_id === row.fabric_type)}
                                        value={settingMainFabrics.find(opt => opt.id === row.main_fabric) || null}
                                        onChange={selected => handleFabricRowChange(idx, 'main_fabric', selected ? selected.id : '')}
                                        isDisabled={!row.fabric_type} // disable until a fabric type is selected
                                        styles={selectErrorStyles}
                                    />
                                </td>
                                <td>
                                    <Select
                                        placeholder="Select Sub Fabric"
                                        isMulti
                                        isDisabled={!row.main_fabric} // disable until a main fabric is selected
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        options={settingSubFabrics.filter(opt => opt.main_fabric_id === row.main_fabric)}
                                        value={settingSubFabrics.filter(opt => (row.sub_fabric || []).includes(opt.id))}
                                        onChange={selected => handleFabricRowChange(idx, 'sub_fabric', selected ? selected.map(s => s.id) : [])}
                                        styles={selectErrorStyles}
                                    />
                                </td>
                                <td>
                                    {(row.compositions || []).map((comp, cidx) => (
                                        <label className="badge badge-light mr-l mb-1" key={cidx}>{settingCommodities.find(item => item.id === comp)?.name || comp}</label>
                                    ))}
                                </td>
                                <td className="text-center">
                                    <Button 
                                        color="danger" 
                                        size="sm" 
                                        onClick={() => handleRemoveFabricRow(idx)} 
                                    ><i className="fa fa-trash" /></Button>
                                    {isDuplicate && <div className="text-danger small mt-1">Duplicate row</div>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <Button color="success" size="sm" className="mt-2" onClick={handleAddFabricRow}><i className="fa fa-plus" /> Add Row</Button>
        </FormGroup>
    );
};

export default FabricTable;
